import {
    CURRENT_APP_QUERY_PARAMETER,
    GROWAVE_TOKEN_STRATEGY,
    TOKEN_STRATEGY_HEADER_KEY,
    TOKEN_STRATEGY_QUERY_PARAMETER,
} from "@/constants/current_app";
import { ModuleInterface } from "@/interfaces/ModuleInterface";
import {
    customerInfoProviderToken,
    globalLoggerToken,
    growaveConfigurationProviderToken,
    gwStorefrontAppInfoToken,
} from "@/tokens";
import ApiClient from "@lib/ApiClient";
import { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
import { GrowaveConfigurationProviderInterface } from "@lib/GrowaveConfigurationProvider/GrowaveConfigurationProviderInterface";
import { CommonModule } from "@modules/common_module/CommonModule";
import { SDK_MODULES } from "@modules/sdk/constants";
import { SdkModule } from "@modules/sdk/SdkModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import { container, inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import { AuthApiService } from "./api_services/AuthApiService/AuthApiService";
import { AuthenticationInfoProvider } from "./AuthenticationInfoProvider/AuthenticationInfoProvider";
import { ProxyAuthenticator } from "./authenticators/ProxyAuthenticator";
import { SignedAuthenticator } from "./authenticators/SignedAuthenticator";
import { CurrentTokenChecker } from "./CurrentTokenChecker";

interface AuthenticationModuleExports {
    authenticationInfoProvider: AuthenticationInfoProvider;
}

@singleton()
export class AuthenticationModule
    implements ModuleInterface<AuthenticationModuleExports>
{
    exports: AuthenticationModuleExports;

    public constructor(
        @inject(SdkModule)
        sdkModule: SdkModule,
        @inject(TokenManagerModule)
        tokenManagerModule: TokenManagerModule,
        @inject(CommonModule)
        commonModule: CommonModule,
        @inject(growaveConfigurationProviderToken)
        growaveConfigurationProvider: GrowaveConfigurationProviderInterface,
        @inject(customerInfoProviderToken)
        customerInfoProvider: CustomerInfoProviderInterface,
        @inject(globalLoggerToken)
        globalLogger: LoggerInterface
    ) {
        const authServiceApiClient = new ApiClient(
            commonModule.exports.baseUrlProvider.appendToBaseUrl(
                "/storefront-api/storefront-authentication-service/v2"
            ),
            {
                [TOKEN_STRATEGY_HEADER_KEY]: GROWAVE_TOKEN_STRATEGY,
            },
            {
                [CURRENT_APP_QUERY_PARAMETER]: container.resolve(
                    gwStorefrontAppInfoToken
                ).currentApp,
                [TOKEN_STRATEGY_QUERY_PARAMETER]: GROWAVE_TOKEN_STRATEGY,
                ...commonModule.exports.designModeQueryParamsProvider
                    .queryParams,
            },
            globalLogger
        );

        const authApiService = new AuthApiService(authServiceApiClient);

        const signedAuthenticator = new SignedAuthenticator(
            authApiService,
            tokenManagerModule,
            growaveConfigurationProvider
        );

        const proxyAuthenticator = new ProxyAuthenticator(
            authApiService,
            tokenManagerModule,
            growaveConfigurationProvider
        );
        void proxyAuthenticator.authenticate();

        const authenticationInfoProvider = new AuthenticationInfoProvider(
            tokenManagerModule.exports.tokenManager
        );

        const currentTokenChecker = new CurrentTokenChecker(
            customerInfoProvider,
            tokenManagerModule,
            globalLogger
        );
        currentTokenChecker.check();

        // Prepare SDK module for dynamic import
        void (async () => {
            const { sdkDynamicModulesManager, sdk } = sdkModule.exports;

            const AuthenticationSdk = await sdkDynamicModulesManager.import(
                SDK_MODULES.AUTHENTICATION
            );
            const authenticationSdk = new AuthenticationSdk(
                tokenManagerModule,
                signedAuthenticator,
                authenticationInfoProvider
            );
            sdk.registerModule(SDK_MODULES.AUTHENTICATION, authenticationSdk);
        })();

        this.exports = {
            authenticationInfoProvider,
        };
    }

    registerProviders() {
        return {};
    }
}
